import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Spin, Alert } from 'antd';
import axios from '../../services/axios';
import projectIcon from "../../assets/icons/projects.png";
import checkIcon from "../../assets/icons/check.png";
import clockIcon from "../../assets/icons/clock.png";

// Stage mapping for Marathi translation of project statuses
const stageMappingMr = {
  under_review: "पुनरावलोकनाधीन",
  planning_stage: "नियोजन टप्पा",
  awaiting_approval: "मंजुरी प्रतीक्षा",
  budget_review: "बजेट पुनरावलोकन",
  site_survey: "साइट सर्वेक्षण",
  feasibility_study: "व्यवहार्यता अभ्यास",
  proposed: "प्रस्तावित",
  ongoing: "सुरू",
  completed: "पूर्ण"
};

// Stage mapping for English display of project statuses
const stageMappingEn = {
  under_review: "Under Review",
  planning_stage: "Planning Stage",
  awaiting_approval: "Awaiting Approval",
  budget_review: "Budget Review",
  site_survey: "Site Survey",
  feasibility_study: "Feasibility Study",
  proposed: "Proposed",
  ongoing: "Ongoing",
  completed: "Completed"
};

// English project data
const projectDataEn = {
  ongoing: [
    { title: "Road Construction Phase II", progress: 70 },
    { title: "Water Supply System", progress: 40 },
    { title: "Community Center Development", progress: 85 },
    { title: "Public Park Development", progress: 25 },
    { title: "School Library Renovation", progress: 60 },
    { title: "Medical Clinic Expansion", progress: 90 },
  ],
  completed: [
    { title: "Community Health Centre" },
    { title: "Primary School Building" },
    { title: "Village Street Lighting" },
    { title: "Water Tank Construction" },
    { title: "Public Toilet Facility" },
    { title: "Village Community Hall" },
  ],
  proposed: [
    { title: "Digital Village Initiative", state: "Under Review" },
    { title: "Waste Management System", state: "Planning Phase" },
    { title: "Solar Power Grid", state: "Awaiting Approval" },
    { title: "Sports Complex", state: "Budget Review" },
    { title: "Women's Training Center", state: "Site Survey" },
    { title: "Agricultural Training Hub", state: "Feasibility Study" },
  ]
};

// Marathi project data
const projectDataMr = {
  ongoing: [
    { title: "रस्ता बांधकाम फेज II", progress: 70 },
    { title: "पाणीपुरवठा यंत्रणा", progress: 40 },
    { title: "समुदायिक केंद्र विकास", progress: 85 },
    { title: "सार्वजनिक उद्यान विकास", progress: 25 },
    { title: "शाळा ग्रंथालय नूतनीकरण", progress: 60 },
    { title: "वैद्यकीय दवाखाना विस्तार", progress: 90 },
  ],
  completed: [
    { title: "समुदायिक आरोग्य केंद्र" },
    { title: "प्राथमिक शाळेची इमारत" },
    { title: "गावातील रस्त्यावरील दिवे" },
    { title: "पाणी टाकी बांधकाम" },
    { title: "सार्वजनिक शौचालय सुविधा" },
    { title: "गावातील समुदायिक हॉल" },
  ],
  proposed: [
    { title: "डिजिटल गाव उपक्रम", state: "पुनरावलोकनाधीन" },
    { title: "कचरा व्यवस्थापन प्रणाली", state: "नियोजन टप्पा" },
    { title: "सौर ऊर्जा ग्रिड", state: "मंजुरीची प्रतीक्षा" },
    { title: "क्रीडा संकुल", state: "बजेट पुनरावलोकन" },
    { title: "महिला प्रशिक्षण केंद्र", state: "साइट सर्वेक्षण" },
    { title: "कृषी प्रशिक्षण केंद्र", state: "व्यवहार्यता अभ्यास" },
  ]
};

const ProgressLoader = ({ progress }) => {
  return (
    <div className="flex items-center gap-3 w-full h-[16px]">
      <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="h-2 rounded-full bg-[#B77224]"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      <span className="text-sm font-medium text-gray-700">{progress}%</span>
    </div>
  );
};

const ProjectCard = ({ type, projects, title, bgColor, gradientFrom, gradientTo, icon: IconComponent, statusText }) => {
  return (
    <div className={`border border-[#DDDDDD] rounded-md p-4 sm:p-5 md:p-6 ${bgColor} flex flex-col gap-2`}>
      <h3
        className="flex justify-center items-center font-medium text-white text-[18px] py-3 px-2 rounded-lg border"
        style={{
          background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`
        }}
      >
        {title}
      </h3>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {projects.map((project, index) => (
          <li key={index} className="p-3 bg-white rounded-md flex flex-col justify-between h-full">
            <h2 className="text-base font-medium mb-2">{project.title}</h2>
            {type === 'ongoing' && (
              <div className="mt-auto">
                <h2 className="text-sm font-normal mb-1">प्रगती</h2>
                <ProgressLoader progress={project.progress} />
              </div>
            )}
            {type === 'completed' && (
              <div className="flex gap-2 items-center mt-auto">
                <img
                  src={checkIcon}
                  alt="check-icon"
                  className="w-4 h-4 relative bottom-1"
                />
                <h2 className="text-sm font-normal">{statusText}</h2>
              </div>
            )}
            {type === 'proposed' && (
              <div className="flex gap-2 items-center mt-auto">
                <img
                  src={clockIcon}
                  alt="clock-icon"
                  className="w-4 h-4 relative bottom-1"
                />
                <h2 className="text-sm font-medium">{project.state}</h2>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ImportantProjects = () => {
  const { t, i18n } = useTranslation();

  // Local state for dynamic projects
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remoteProjects, setRemoteProjects] = useState(null);

  // Get project data based on current language — fallback to static if remote not available
  const projectData = remoteProjects || (i18n.language === 'mr' ? projectDataMr : projectDataEn);

  // Get the appropriate stage mapping based on language
  const getStatusDisplay = (status) => {
    const stageMapping = i18n.language === 'mr' ? stageMappingMr : stageMappingEn;
    return stageMapping[status] || status;
  };

  useEffect(() => {
    let mounted = true;
    const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
    const stageMapping = i18n.language === 'mr' ? stageMappingMr : stageMappingEn;

    const fetchProjects = async () => {
      setLoading(true);
      setError(null);

      try {
        const resp = await axios.get('/projects/', { params: { language: languageParam } });
        console.log(resp.data);
        const data = Array.isArray(resp.data)
          ? resp.data
          : (resp.data.results || resp.data.data || Object.values(resp.data || {}));

        if (!mounted) return;

        if (Array.isArray(data) && data.length > 0) {
          // Map incoming projects into grouped structure expected by the component
          // Sort by order field in increasing order
          const ongoing = data
            .filter(p => p.status === 'ongoing')
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(p => ({ title: p.title, progress: p.progress || 0 }));

          const completed = data
            .filter(p => p.status === 'completed')
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(p => ({ title: p.title }));

          // proposed = those not ongoing/completed - use stageMapping for display
          const proposed = data
            .filter(p => !['ongoing', 'completed'].includes(p.status))
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(p => ({
              title: p.title,
              state: stageMapping[p.status] || p.status || ''
            }));

          setRemoteProjects({ ongoing, completed, proposed });
        } else {
          // no remote data — leave remoteProjects null to use fallback
          setRemoteProjects(null);
        }
      } catch (err) {
        console.error('Failed to fetch projects', err);
        if (mounted) setError(err.message || 'Failed to load projects');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchProjects();

    return () => { mounted = false; };
  }, [i18n.language]);

  // Define project card configurations
  const projectCardConfigs = [
    {
      type: 'ongoing',
      title: t('importantProjects.ongoingTitle'),
      bgColor: 'bg-[#FEF9EB]',
      gradientFrom: '#B77224',
      gradientTo: '#573000',
      projects: projectData.ongoing,
      statusText: t('importantProjects.progress')
    },
    {
      type: 'completed',
      title: t('importantProjects.completedTitle'),
      bgColor: 'bg-[#EDFDF5]',
      gradientFrom: '#00C950',
      gradientTo: '#006D29',
      projects: projectData.completed,
      statusText: t('importantProjects.completed')
    },
    {
      type: 'proposed',
      title: t('importantProjects.proposedTitle'),
      bgColor: 'bg-[#F2F3FF]',
      gradientFrom: '#2B7FFF',
      gradientTo: '#002371',
      projects: projectData.proposed,
      statusText: ''
    }
  ];

  return (
    <div className="border rounded-md p-4 sm:p-5 md:p-7 bg-white flex flex-col gap-2">
      {/* title */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-3 items-center">
          <img
            className="w-10 h-10 relative"
            src={projectIcon}
            alt={t('importantProjects.iconAlt')}
          />
          <h2 className="font-semibold w-full h-fit m-0 text-2xl max-sm:text-xl">{t('importantProjects.title')}</h2>
        </div>
        <p className="font-normal text-base">
          {t('importantProjects.description')}
        </p>
      </div>
      {loading && (
        <div className="py-6">
          <Spin />
        </div>
      )}

      {error && (
        <div className="py-4">
          <Alert type="error" message={t('Failed to load projects') || 'Failed to load projects'} description={error} />
        </div>
      )}
      {/* project cards */}
      <div className="flex flex-col gap-4">
        {projectCardConfigs.map((config, index) => (
          <ProjectCard
            key={index}
            type={config.type}
            projects={config.projects}
            title={config.title}
            bgColor={config.bgColor}
            gradientFrom={config.gradientFrom}
            gradientTo={config.gradientTo}
            statusText={config.statusText}
          />
        ))}
      </div>
    </div>
  );
};

export default ImportantProjects;
